import React, { useCallback, useMemo, FC } from "react";
import { SettingsTabBarItemContent } from "./settings-tab-bar.types";
import {
  MDSBadge,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSTooltipTrigger,
} from "@/mds";
import styled from "@emotion/styled";

export interface SettingsTabBarItemProps {
  selectedItemId: string;
  item: SettingsTabBarItemContent;
}

export const SettingsTabBarItem: FC<SettingsTabBarItemProps> = ({ item, selectedItemId }) => {
  const { onClick: onItemClick, id: itemId } = item;

  const handleClick = useCallback(() => onItemClick({ itemId }), [onItemClick, itemId]);
  const isSelected = useMemo(() => item.id === selectedItemId, [item.id, selectedItemId]);
  const preserveBadgeVariant = item.badgeVariant !== undefined;

  const button = (
    <StyledWrapper
      $isSelected={isSelected}
      $preserveBadgeVariant={preserveBadgeVariant}
      onClick={handleClick}
    >
      {item.icon && <MDSIcon kind={item.icon} color={MDSIconColor.Secondary} size={12} />}
      <StyledLabel variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Primary}>
        {item.label}
      </StyledLabel>
      {item.badgeLabel && (
        <StyledBadge
          label={item.badgeLabel}
          variant={item.badgeVariant}
          $isSelected={isSelected}
          $preserveVariant={preserveBadgeVariant}
        />
      )}
      <StyledIcon kind={MDSIconKind.AngleDown} $isSelected={isSelected} />
    </StyledWrapper>
  );

  if (!item.tooltipLabel) return button;

  return (
    <MDSTooltip
      config={{
        label: item.tooltipLabel,
        placement: MDSTooltipPlacement.Right,
      }}
    >
      <StyledTooltipTrigger tabIndex={-1}>{button}</StyledTooltipTrigger>
    </MDSTooltip>
  );
};

const StyledTooltipTrigger = styled(MDSTooltipTrigger)`
  width: 100%;
`;

const StyledIcon = styled(MDSIcon)<{ $isSelected: boolean }>(({ $isSelected }) => ({
  display: "none",
  "@container mds-panel (max-width: 750px)": {
    display: $isSelected ? "block" : undefined,
  },
}));

const StyledWrapper = styled.button<{
  $isSelected: boolean;
  $preserveBadgeVariant: boolean;
}>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  width: 100%;
  background-color: ${({ theme, $isSelected }) =>
    $isSelected
      ? theme.colors.background.menu.selected
      : theme.colors.background.button.transparent.default};
  transition: background-color 0.08s ease;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[5]}`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }
  ${({ theme, $preserveBadgeVariant }) =>
    !$preserveBadgeVariant &&
    `
      &:hover [data-mds-name="badge"] {
        background-color: ${theme.colors.background.button.contrastAccent.default};
      }
    `}
  &:active {
    background-color: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }

  @container mds-panel (max-width: 750px) {
    display: flex;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius.xlarge};
    background-color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.colors.background.button.default.default : undefined};
    padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[5]}`};
    ${({ $isSelected }) => !$isSelected && `display: none;`}
  }
`;

const StyledLabel = styled(MDSText)`
  user-select: none;
  text-align: start;

  @container mds-panel (max-width: 750px) {
    font-weight: ${({ theme }) => theme.fontWeights.smedium};
    line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  }
`;

const StyledBadge = styled(MDSBadge)<{
  $isSelected: boolean;
  $preserveVariant: boolean;
}>`
  margin-left: auto;
  background-color: ${({ theme, $isSelected, $preserveVariant }) =>
    $isSelected && !$preserveVariant
      ? theme.colors.background.button.contrastAccent.default
      : undefined};

  @container mds-panel (max-width: 750px) {
    display: none;
  }
`;
